import {partition} from '@benchling/js-utils';
import gql from 'graphql-tag';
import React from 'react';
import {connect} from 'react-redux';

import * as Analytics from 'analytics/Analytics';
import {structuredTableActionOrigin} from 'analytics/Util';
import * as ArchiveActions from 'archive/ArchiveActions';
import ArchiveModal, {ArchiveModalRenderTableOverrideArgs} from 'archive/components/modals/ArchiveModal';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {
  ActionMethod,
  AnalyticsMetadataForContext,
  STORABLE_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {CICOItemIdsToAssignee} from 'constants/SampleStatusConstants';
import {TargetStorable} from 'folder-items/FileConstants';
import {readQuerySafe} from 'graphql-client/ApolloWrappers';
import BenchlingQuery, {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {assertAllAccessible} from 'graphql-client/InaccessibleInfo';
import CheckInModal from 'inventory/check-in-check-out/CheckInModal';
import CheckOutModal from 'inventory/check-in-check-out/CheckOutModal';
import * as CICOActions from 'inventory/check-in-check-out/CICOActions';
import ReserveModal from 'inventory/check-in-check-out/ReserveModal';
import {getRestrictedSampleUsersHeaderText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {
  doesItemSupportSampleStatus,
  isFixedPlateWell,
} from 'inventory/sample-status/SampleStatusPresentationUtils';
import {conditionallyRenderSampleStatusWarning} from 'inventory/sample-status/SampleStatusWarning';
import PrintBarcodeModal from 'label-printing/PrintBarcodeModal';
import {
  ContainerInventoryActionOptionQuery,
  ContainerInventoryActionOptionQueryVariables,
} from 'notebook/structured-tables/action-functions/__generated/ContainerInventoryActionOptionQuery';
import {CurrentTenantIdAndUserIdQuery} from 'notebook/structured-tables/action-functions/__generated/CurrentTenantIdAndUserIdQuery';
import makeCustomButtonActionFunction from 'notebook/structured-tables/action-functions/makeCustomButtonActionFunction';
import {StructuredTableEditStatus} from 'notebook/structured-tables/action-functions/toggle-edit-mode/reduceToggledTableEditState';
import {getStructuredTableTogglableStatus} from 'notebook/structured-tables/action-functions/toggle-edit-mode/StructuredTableTogglableStateActions';
import unsupportedOverflowMenuItem from 'notebook/structured-tables/action-functions/unsupportedOverflowMenuItem';
import {
  isContainersStructuredTableColumn,
  isStorableStructuredTableColumn,
} from 'notebook/structured-tables/columns/TypePredicates';
import {
  ColumnWithPositionalIndex,
  LiveRowMetadata,
  StructuredTable,
  StructuredTableColumn,
  StructuredTableRow,
  ToolbarButtonActionFunction,
} from 'notebook/structured-tables/StructuredTableConstants';
import {isInExecutableCanvas} from 'notebook/structured-tables/util/isInExecutableCanvas';
import {EvaluatedCell, EvaluatedTable} from 'notebook/tables/excess/Evaluator';
import {TableColumnType, TableIndicatorStatus} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {getEntryOrFileIdKey} from 'notebook/util/analytics/FileAnalyticsUtils';
import {BenchlingState} from 'redux-stores/reduceApp';
import MoveStorablesFromStructuredTableModal from 'samples/MoveStorablesFromStructuredTableModal';
import {getMoveStorablesTooltip, shouldDisableMoveStorablesAction} from 'samples/storables/MoveStorablesUtil';
import BulkUpdateRestrictedSampleUsersModal from 'samples/toolbar/BulkUpdateRestrictedSampleUsersModal';
import {getUpdateRestrictedSampleUsersDisabledMessage} from 'samples/toolbar/UpdateSampleRolesMenuItems';
import {FieldType, isRequiredLinkStorableSchemaOrAnyInventory} from 'schemas/fields/SchemaFieldConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu, {ROLES} from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import OptionContainer from 'ui-library/menus/OptionContainer';
import OptionContent from 'ui-library/menus/OptionContent';
import Section from 'ui-library/menus/Section';
import SectionHeader from 'ui-library/menus/SectionHeader';
import APIUtil from 'util/APIUtil';
import {Optional} from 'util/Types';

type ContainerGQL = ExtractByTypename<
  ContainerInventoryActionOptionQuery['storables'][number],
  'ContainerNode'
>;

export enum InventoryAction {
  UPDATE_RESTRICTED_SAMPLE_USERS = 'UPDATE_RESTRICTED_SAMPLE_USERS',
  RESERVE = 'RESERVE',
  CHECK_OUT = 'CHECK_OUT',
  CHECK_IN = 'CHECK_IN',
  MOVE_STORABLES = 'MOVE_STORABLES',
  PRINT_LABELS = 'PRINT_LABELS',
  ARCHIVE = 'ARCHIVE',
}

export const CURRENT_TENANT_ID_AND_USER_ID_QUERY = gql`
  query CurrentTenantIdAndUserIdQuery @skipAudit @engComponent(name: INVENTORY) {
    currentTenant {
      id
    }
    currentUser {
      ... on UserNode {
        id
      }
      ... on BenchlingAppNode {
        id
      }
      ... on ServiceAccountActorNode {
        id
      }
    }
  }
`;

export interface TargetStorableNoIcon extends Omit<TargetStorable, 'iconId'> {}

export interface TargetRowAndStorables<TRowMetadata> {
  // Perform inventory action on these storables
  targetStorables: Array<TargetStorableNoIcon>;
  // Which row these storables came from
  structuredTableRow: StructuredTableRow<TRowMetadata>;
}

export interface InventoryActionHandlerArgs<TTableMetadata, TRowMetadata> {
  targetStorableColumnWithIndex: ColumnWithPositionalIndex;
  // Only finalized rows that have target storables present will be included
  targetRowsAndStorables: Array<TargetRowAndStorables<TRowMetadata>>;
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
  analyticsMetadata: AnalyticsMetadataForContext;
}

export function loadContainersFromCacheForUpdatingRestrictedSampleUsers(
  targetRowsAndStorables: Array<TargetRowAndStorables<unknown>>
): Array<ContainerGQL> {
  const storableIds = targetRowsAndStorables.flatMap(({targetStorables}) =>
    targetStorables.map(({id}) => id)
  );
  const result = readQuerySafe<
    ContainerInventoryActionOptionQuery,
    ContainerInventoryActionOptionQueryVariables
  >({query: CONTAINER_INVENTORY_ACTION_OPTION_QUERY}, {variables: {storableIds}});
  assert(
    result != null,
    `Expected ContainerInventoryActionOptionQuery result to be in cache because it was fetched for the menu item. storableIds=${JSON.stringify(
      storableIds
    )}`
  );

  const storables = assertAllAccessible(result.storables);
  const containers: Array<ContainerGQL> = storables.filter((storable): storable is ContainerGQL =>
    APIUtil.isContainer(storable.id)
  );
  assert(
    containers.length === storables.length,
    'Expected loadContainersFromCacheForUpdatingRestrictedSampleUsers to be called only with containers, because the menu item should have been disabled if any items were not containers.'
  );
  return containers;
}

const INVENTORY_ACTION_HANDLERS: {
  [k in InventoryAction]: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    modalComponent: React.FunctionComponent<any> | React.ComponentClass<any, any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    additionalModalArgsFunc?: (args: InventoryActionHandlerArgs<unknown, unknown>) => {[k: string]: any};
  };
} = {
  [InventoryAction.CHECK_OUT]: {
    modalComponent: CheckOutModal,
    additionalModalArgsFunc: (args) => ({
      onCheckOut: (itemIdsToAssignee: CICOItemIdsToAssignee, comment?: string): void => {
        CICOActions.checkOut(itemIdsToAssignee, {comment});
      },
    }),
  },
  [InventoryAction.CHECK_IN]: {
    modalComponent: CheckInModal,
    additionalModalArgsFunc: (args) => ({
      onCheckIn: (itemIds: Array<string>) => {
        CICOActions.checkIn(itemIds, {});
      },
    }),
  },
  [InventoryAction.RESERVE]: {
    modalComponent: ReserveModal,
    additionalModalArgsFunc: (args) => ({
      onReserve: (itemIdsToAssignee: CICOItemIdsToAssignee, comment?: string): void => {
        CICOActions.reserve(itemIdsToAssignee, {comment});
      },
    }),
  },
  [InventoryAction.MOVE_STORABLES]: {modalComponent: MoveStorablesFromStructuredTableModal},
  [InventoryAction.UPDATE_RESTRICTED_SAMPLE_USERS]: {
    modalComponent: BulkUpdateRestrictedSampleUsersModal,
    additionalModalArgsFunc: (args) => ({
      items: loadContainersFromCacheForUpdatingRestrictedSampleUsers(args.targetRowsAndStorables),
    }),
  },
  [InventoryAction.ARCHIVE]: {
    modalComponent: ArchiveModal,
    additionalModalArgsFunc: (args) => ({
      items: args.targetRowsAndStorables.flatMap(
        (targetRowAndStorables) => targetRowAndStorables.targetStorables
      ),
      onArchive: ArchiveActions.bulkArchiveStorablesWithDataMods,
    }),
  },
  [InventoryAction.PRINT_LABELS]: {modalComponent: PrintBarcodeModal},
};

export function createInventoryActionHandlerFunction<TProps>(
  modalComponent: React.FunctionComponent<TProps> | React.ComponentClass<TProps, unknown>,
  additionalModalArgsFunc?: (args: InventoryActionHandlerArgs<unknown, unknown>) => Partial<TProps>,
  kwargs: Optional<Partial<TProps>> = undefined
): (args: InventoryActionHandlerArgs<unknown, unknown>) => void {
  return (args: InventoryActionHandlerArgs<unknown, unknown>) =>
    // @ts-expect-error - Type checks do not recognize that TProps is equivalent the modal's props
    legacyRunModal<TProps>(modalComponent, {
      ...args,
      items: args.targetRowsAndStorables.flatMap(({targetStorables}) =>
        targetStorables.map(({id}) => ({id}))
      ),
      itemIds: args.targetRowsAndStorables.flatMap(({targetStorables}) => targetStorables.map(({id}) => id)),
      ...additionalModalArgsFunc?.(args),
      ...kwargs,
    });
}

export function getDefaultInventoryActionHandlerWithKwargs(
  actionKey: InventoryAction,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  kwargs: Optional<{[k: string]: any}> = undefined
): (args: InventoryActionHandlerArgs<unknown, unknown>) => void {
  const {modalComponent, additionalModalArgsFunc} = INVENTORY_ACTION_HANDLERS[actionKey];
  return createInventoryActionHandlerFunction(modalComponent, additionalModalArgsFunc, kwargs);
}

export function archiveActionTableOverride(args: ArchiveModalRenderTableOverrideArgs): JSX.Element {
  return (
    <>
      {args.hasHiddenDescendantItems && (
        <div className="u-small-spacing-bottom">
          <InlineBanner
            bannerType={InlineBannerType.INFO}
            text="Any inventory items contained within the selected items will also be archived."
          />
        </div>
      )}
      {conditionallyRenderSampleStatusWarning({
        shouldShowStatusWarning: args.shouldShowStatusWarning,
        samples: args.samples,
        trackingParams: args.trackingParams,
        selectedSampleIds: args.itemIdsToSubmit,
        setSelectedSampleIds: args.setItemIdsToSubmit,
        shouldProceed: args.shouldProceed,
        setShouldProceed: args.setShouldProceed,
        canSelectMultiple: args.showItemCheckboxes,
      })}
    </>
  );
}

function defaultArchiveActionHandler(args: InventoryActionHandlerArgs<unknown, unknown>): void {
  getDefaultInventoryActionHandlerWithKwargs(InventoryAction.ARCHIVE, {
    renderArchivableItemTableOverride: archiveActionTableOverride,
    checkForSampleStatusWarningsParamOverride: {shouldAlwaysShowWarning: true},
    showItemCheckboxes: true,
  })(args);
}

function defaultCheckInActionHandler(args: InventoryActionHandlerArgs<unknown, unknown>): void {
  getDefaultInventoryActionHandlerWithKwargs(InventoryAction.CHECK_IN, {
    shouldAlwaysShowSampleStatusTable: true,
  })(args);
}

function defaultPrintLabelsActionHandler(args: InventoryActionHandlerArgs<unknown, unknown>): void {
  getDefaultInventoryActionHandlerWithKwargs(InventoryAction.PRINT_LABELS, {
    shouldAlwaysShowSampleStatusTable: true,
  })(args);
}

function defaultCheckOutActionHandler(args: InventoryActionHandlerArgs<unknown, unknown>): void {
  getDefaultInventoryActionHandlerWithKwargs(InventoryAction.CHECK_OUT, {
    shouldAlwaysShowSampleStatusTable: true,
  })(args);
}

function defaultReserveActionHandler(args: InventoryActionHandlerArgs<unknown, unknown>): void {
  getDefaultInventoryActionHandlerWithKwargs(InventoryAction.RESERVE, {
    shouldAlwaysShowSampleStatusTable: true,
  })(args);
}

function defaultMoveStorablesActionHandler(args: InventoryActionHandlerArgs<unknown, unknown>): void {
  getDefaultInventoryActionHandlerWithKwargs(InventoryAction.MOVE_STORABLES, {
    shouldAlwaysShowSampleStatusTable: true,
  })(args);
}

function defaultUpdateRestrictedSampleUsersActionHandler(
  args: InventoryActionHandlerArgs<unknown, unknown>
): void {
  getDefaultInventoryActionHandlerWithKwargs(InventoryAction.UPDATE_RESTRICTED_SAMPLE_USERS, {
    shouldAlwaysShowSampleStatusTable: true,
  })(args);
}

export const INVENTORY_ACTION_TO_DEFAULT_HANDLER: {
  [actionKey in InventoryAction]: (handlerArgs: InventoryActionHandlerArgs<unknown, unknown>) => void;
} = {
  [InventoryAction.UPDATE_RESTRICTED_SAMPLE_USERS]: defaultUpdateRestrictedSampleUsersActionHandler,
  [InventoryAction.RESERVE]: defaultReserveActionHandler,
  [InventoryAction.CHECK_OUT]: defaultCheckOutActionHandler,
  [InventoryAction.CHECK_IN]: defaultCheckInActionHandler,
  [InventoryAction.MOVE_STORABLES]: defaultMoveStorablesActionHandler,
  [InventoryAction.PRINT_LABELS]: defaultPrintLabelsActionHandler,
  [InventoryAction.ARCHIVE]: defaultArchiveActionHandler,
};

export interface InventoryActionOnStructuredTableButtonProps<TTableMetadata, TRowMetadata> {
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
  computedTable: EvaluatedTable;
  liveRowMetadataByRow?: Array<LiveRowMetadata>;
  // Use this if you want functionality specific to your kind of structured table, e.g. using task creators
  // from a process task structured table. We should not hard-code implementation details for specific kinds
  // of structured tables into this file!
  customInventoryActionHandlers?: {
    [actionKey in InventoryAction]?: (
      handlerArgs: InventoryActionHandlerArgs<TTableMetadata, TRowMetadata>
    ) => void;
  };
  tableStatus?: TableIndicatorStatus;
  forceEnableButton?: boolean;
  analyticsMetadata: AnalyticsMetadataForContext;
  currentTenantId: string;
  currentUserId: string;
  entryOrTemplateId: string | null;
  hiddenOptions?: Array<InventoryAction>;
  hiddenColumnTypes?: Array<TableColumnType>;
}

function renderNoTargetStorableColumnsFoundSubmenu(): JSX.Element {
  return (
    <Menu dropLeft={true}>
      <Section>
        <SectionHeader>Column</SectionHeader>
        <Option isDisabled={true}>No valid inventory columns found</Option>
      </Section>
    </Menu>
  );
}

function getTargetableStorableColumns(
  allColumns: Array<StructuredTableColumn>,
  hiddenColumnTypes?: Array<TableColumnType>
): Array<ColumnWithPositionalIndex> {
  return allColumns
    .map((column, columnIndex) => ({column, columnIndex}))
    .filter(
      ({column}) =>
        isStorableStructuredTableColumn(column) &&
        (hiddenColumnTypes ? !hiddenColumnTypes.includes(column.columnType) : true)
    );
}

function getTargetRowAndStorables<TRowMetadata>(
  structuredTableRow: StructuredTableRow<TRowMetadata>,
  columnWithIndex: ColumnWithPositionalIndex,
  computedTableRow: Array<EvaluatedCell>
): TargetRowAndStorables<TRowMetadata> {
  const targetTypedValue =
    computedTableRow[columnWithIndex.columnIndex]?.value ??
    structuredTableRow.cells[columnWithIndex.columnIndex].typedValue;
  if (targetTypedValue?.type === FieldType.STORABLE_LINK) {
    return {targetStorables: targetTypedValue.targetStorables, structuredTableRow};
  }
  if (targetTypedValue?.type === 'STORABLES') {
    return {targetStorables: targetTypedValue.storableSummaries, structuredTableRow};
  }
  return {targetStorables: [], structuredTableRow};
}

export const CONTAINER_INVENTORY_ACTION_OPTION_QUERY = gql`
  query ContainerInventoryActionOptionQuery($storableIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    storables(ids: $storableIds) {
      ... on StorableInterface {
        id
        name
        permissions {
          writable
          readable
          appendable
          admin
          canEditRestrictedSampleUsers
          canRestrictSamples
        }
      }
      ... on ContainerNode {
        id
        name
        sampleRoles {
          id
          isCurrentUserSampleOwner
          sampleOwners {
            ... on PartyInterface {
              id
              handle
              name
              avatarUrl
            }
          }
          restrictedSampleUsers {
            ... on PartyInterface {
              id
              handle
              name
              avatarUrl
            }
          }
        }
        restrictionStatus
        parentStorable {
          ... on StorableInterface {
            id
            storableSchema {
              id
              ... on PlateSchemaNode {
                id
                plateSchemaType
              }
            }
          }
        }
      }
      ...HasArchiveRecordGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function UpdateRestrictedSampleUsersOption<TTableMetadata, TRowMetadata>(props: {
  itemIds: Array<string>;
  value: InventoryActionHandlerArgs<TTableMetadata, TRowMetadata>;
  onSelect: ((value?: InventoryActionHandlerArgs<TTableMetadata, TRowMetadata>) => void) | null;
  children: React.ReactNode;
}): JSX.Element {
  const result = useBenchlingQuery<
    ContainerInventoryActionOptionQuery,
    ContainerInventoryActionOptionQueryVariables
  >(CONTAINER_INVENTORY_ACTION_OPTION_QUERY, {variables: {storableIds: props.itemIds}});

  const sampleStatusItems =
    result.type === QueryResultType.SUCCESS ? assertAllAccessible(result.data.storables) : [];
  const tooltip = getUpdateRestrictedSampleUsersDisabledMessage({
    sampleStatusItems: sampleStatusItems.map((item) => ({
      ...item,
      permissions: {
        ...item.permissions,
        canEditRestrictedSampleUsers: Boolean(item.permissions.canEditRestrictedSampleUsers),
        canRestrictSamples: Boolean(item.permissions.canRestrictSamples),
      },
    })),
    isLoading: result.type !== QueryResultType.SUCCESS,
  });

  return (
    <OptionContainer isDisabled={tooltip != null} onSelect={props.onSelect} value={props.value}>
      <OptionContent tooltipTitle={tooltip ?? undefined}>{props.children}</OptionContent>
    </OptionContainer>
  );
}

UpdateRestrictedSampleUsersOption.defaultProps = {
  _role: ROLES.OPTION,
};

function CICOOption<TTableMetadata, TRowMetadata>(props: {
  itemIds: Array<string>;
  value: InventoryActionHandlerArgs<TTableMetadata, TRowMetadata>;
  onSelect: ((value?: InventoryActionHandlerArgs<TTableMetadata, TRowMetadata>) => void) | null;
  children: React.ReactNode;
}): JSX.Element {
  const result = useBenchlingQuery<
    ContainerInventoryActionOptionQuery,
    ContainerInventoryActionOptionQueryVariables
  >(CONTAINER_INVENTORY_ACTION_OPTION_QUERY, {
    variables: {storableIds: props.itemIds.filter((id) => APIUtil.isStorable(id))},
  });

  const items = result.type === QueryResultType.SUCCESS ? assertAllAccessible(result.data.storables) : [];
  const hasNonStorables = items.length !== props.itemIds.length;
  const containsWellsOrNonContainers = items.some(
    (item) =>
      !doesItemSupportSampleStatus({
        ...item,
        permissions: {
          ...item.permissions,
          canEditRestrictedSampleUsers: Boolean(item.permissions.canEditRestrictedSampleUsers),
          canRestrictSamples: Boolean(item.permissions.canRestrictSamples),
        },
      })
  );
  const hasArchivedItems = items.some((item) => isArchived(item));

  const tooltip =
    result.type !== QueryResultType.SUCCESS
      ? 'Loading...'
      : containsWellsOrNonContainers || hasNonStorables
        ? 'Only containers can be checked in / checked out'
        : hasArchivedItems
          ? 'Archived containers cannot be checked in / checked out'
          : null;
  return (
    <OptionContainer isDisabled={tooltip != null} onSelect={props.onSelect} value={props.value}>
      <OptionContent tooltipTitle={tooltip ?? undefined}>{props.children}</OptionContent>
    </OptionContainer>
  );
}

CICOOption.defaultProps = {
  _role: ROLES.OPTION,
};

function ArchiveOption<TTableMetadata, TRowMetadata>(props: {
  itemIds: Array<string>;
  actionHandlerArgs: InventoryActionHandlerArgs<TTableMetadata, TRowMetadata>;
  onSelect: ((actionHandlerArgs?: InventoryActionHandlerArgs<TTableMetadata, TRowMetadata>) => void) | null;
  children: React.ReactNode;
}): JSX.Element {
  const result = useBenchlingQuery<
    ContainerInventoryActionOptionQuery,
    ContainerInventoryActionOptionQueryVariables
  >(CONTAINER_INVENTORY_ACTION_OPTION_QUERY, {variables: {storableIds: props.itemIds}});

  const items = result.type === QueryResultType.SUCCESS ? assertAllAccessible(result.data.storables) : [];
  const hasFixedPlateWells = items.some((item) => isFixedPlateWell(item as ContainerGQL));
  const archivedItems = items.filter((item) => isArchived(item));
  const hasAllArchivedItems = archivedItems.length === items.length;

  const tooltip =
    result.type !== QueryResultType.SUCCESS
      ? 'Loading...'
      : hasAllArchivedItems
        ? 'Inventory items already archived'
        : hasFixedPlateWells
          ? 'Well plate wells cannot be independently archived'
          : null;

  // eslint-disable-next-line benchlint/no-spread-reduce
  const itemIdsToArchiveRecord: {[itemId: string]: string} = items.reduce(
    (obj, item) => ({...obj, [item.id]: item.archiveRecord}),
    {}
  );

  const onSelectAddArchiveRecord = (
    actionHandlerArgs?: InventoryActionHandlerArgs<TTableMetadata, TRowMetadata>
  ): void => {
    if (props.onSelect == null) {
      return;
    }
    let args = actionHandlerArgs;
    if (actionHandlerArgs != null && archivedItems.length > 0) {
      args = {
        ...actionHandlerArgs,
        targetRowsAndStorables: actionHandlerArgs.targetRowsAndStorables.map((targetRowAndStorables) => ({
          ...targetRowAndStorables,
          targetStorables: targetRowAndStorables.targetStorables.map((targetStorable) => ({
            ...targetStorable,
            archiveRecord: itemIdsToArchiveRecord[targetStorable.id],
          })),
        })),
      };
    }
    props.onSelect(args);
  };
  return (
    <OptionContainer
      isDisabled={tooltip != null}
      onSelect={onSelectAddArchiveRecord}
      value={props.actionHandlerArgs}
    >
      <OptionContent tooltipTitle={tooltip ?? undefined}>{props.children}</OptionContent>
    </OptionContainer>
  );
}

ArchiveOption.defaultProps = {
  _role: ROLES.OPTION,
};

export function MoveStorablesOption<TTableMetadata, TRowMetadata>(props: {
  itemIds: Array<string>;
  value: InventoryActionHandlerArgs<TTableMetadata, TRowMetadata>;
  onSelect: ((value?: InventoryActionHandlerArgs<TTableMetadata, TRowMetadata>) => void) | null;
  children: React.ReactNode;
}): JSX.Element {
  const result = useBenchlingQuery<
    ContainerInventoryActionOptionQuery,
    ContainerInventoryActionOptionQueryVariables
  >(CONTAINER_INVENTORY_ACTION_OPTION_QUERY, {
    variables: {storableIds: props.itemIds.filter((id) => APIUtil.isStorable(id))},
  });

  let isDisabled;
  let tooltip;
  if (result.type === QueryResultType.SUCCESS) {
    const storables = assertAllAccessible(result.data.storables);

    const someStorablesAreSpecified = storables.length > 0;
    const allStorablesAreWritable = storables.every((storable) => storable.permissions.writable);
    const noFixedPlateWellsSpecified = storables
      .filter((storable) => APIUtil.isContainer(storable.id))
      .every((storable) => !isFixedPlateWell(storable as ContainerGQL));

    isDisabled = shouldDisableMoveStorablesAction(
      someStorablesAreSpecified,
      allStorablesAreWritable,
      noFixedPlateWellsSpecified
    );
    tooltip = getMoveStorablesTooltip(
      someStorablesAreSpecified,
      allStorablesAreWritable,
      noFixedPlateWellsSpecified
    );
  } else {
    isDisabled = true;
    tooltip = 'Loading...';
  }

  return (
    <OptionContainer isDisabled={isDisabled} onSelect={props.onSelect} value={props.value}>
      <OptionContent tooltipTitle={isDisabled ? tooltip : undefined}>{props.children}</OptionContent>
    </OptionContainer>
  );
}

MoveStorablesOption.defaultProps = {
  _role: ROLES.OPTION,
};

interface ConnectedProps {
  editStatus: StructuredTableEditStatus;
}

function mapStateToProps<TTableMetadata, TRowMetadata>(
  state: BenchlingState,
  props: InventoryActionOnStructuredTableButtonProps<TTableMetadata, TRowMetadata>
): ConnectedProps {
  return {
    editStatus: getStructuredTableTogglableStatus(props.structuredTable.cid),
  };
}

export const InventoryActionOnStructuredTableButton = connect(mapStateToProps)(
  UnconnectedInventoryActionOnStructuredTableButton
);
InventoryActionOnStructuredTableButton.displayName = 'InventoryActionOnStructuredTableButton';

function UnconnectedInventoryActionOnStructuredTableButton<TTableMetadata, TRowMetadata>(
  props: InventoryActionOnStructuredTableButtonProps<TTableMetadata, TRowMetadata> & ConnectedProps
): JSX.Element {
  const targetableStorableColumns = getTargetableStorableColumns(
    props.structuredTable.columns,
    props.hiddenColumnTypes
  );

  function renderTargetStorableColumnOption(
    targetStorableColumnWithIndex: ColumnWithPositionalIndex,
    actionKey: InventoryAction
  ): JSX.Element {
    const targetRowsAndStorables: Array<TargetRowAndStorables<TRowMetadata>> = props.structuredTable.rows
      .map((structuredTableRow, idx) =>
        getTargetRowAndStorables(structuredTableRow, targetStorableColumnWithIndex, props.computedTable[idx])
      )
      .filter(
        ({targetStorables}, idx) =>
          targetStorables.length > 0 && !props.liveRowMetadataByRow?.[idx].archivePurpose
      );
    const actionHandlerArgs: InventoryActionHandlerArgs<TTableMetadata, TRowMetadata> = {
      targetStorableColumnWithIndex,
      targetRowsAndStorables,
      structuredTable: props.structuredTable,
      analyticsMetadata: props.analyticsMetadata,
    };

    const onSelectAction = (args: InventoryActionHandlerArgs<TTableMetadata, TRowMetadata>): void => {
      const eventGroupId =
        args.analyticsMetadata?.eventGroupId ??
        Analytics.deprecatedGenerateEventGroupId(props.currentTenantId, props.currentUserId);

      Analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.CLICKED_INVENTORY_SHORTCUT, {
        ...props.analyticsMetadata,
        inventoryShortcutActionChosen: actionKey,
        eventGroupId,
      });

      const actionHandler =
        props.customInventoryActionHandlers?.[actionKey] ?? INVENTORY_ACTION_TO_DEFAULT_HANDLER[actionKey];
      actionHandler({
        ...args,
        analyticsMetadata: {...args.analyticsMetadata, eventGroupId},
      });
    };

    const itemIds = targetRowsAndStorables.flatMap(({targetStorables}) => targetStorables.map(({id}) => id));
    const DEFAULT_PROPS = {
      key: `${actionKey}-${targetStorableColumnWithIndex.column.name}-${targetStorableColumnWithIndex.column.provenanceUuid}`,
      onSelect: onSelectAction,
      value: actionHandlerArgs,
      itemIds,
    };

    if (actionKey === InventoryAction.UPDATE_RESTRICTED_SAMPLE_USERS && itemIds.length > 0) {
      return (
        <UpdateRestrictedSampleUsersOption<TTableMetadata, TRowMetadata> {...DEFAULT_PROPS}>
          {targetStorableColumnWithIndex.column.name}
        </UpdateRestrictedSampleUsersOption>
      );
    }
    if (actionKey === InventoryAction.MOVE_STORABLES && itemIds.length > 0) {
      return (
        <MoveStorablesOption<TTableMetadata, TRowMetadata> {...DEFAULT_PROPS}>
          {targetStorableColumnWithIndex.column.name}
        </MoveStorablesOption>
      );
    }
    if (
      [InventoryAction.CHECK_IN, InventoryAction.CHECK_OUT, InventoryAction.RESERVE].includes(actionKey) &&
      itemIds.length > 0
    ) {
      return (
        <CICOOption<TTableMetadata, TRowMetadata> {...DEFAULT_PROPS}>
          {targetStorableColumnWithIndex.column.name}
        </CICOOption>
      );
    } else if (actionKey === InventoryAction.ARCHIVE && itemIds.length > 0) {
      return (
        <ArchiveOption<TTableMetadata, TRowMetadata>
          key={`${actionKey}-${targetStorableColumnWithIndex.column.name}-${targetStorableColumnWithIndex.column.provenanceUuid}`}
          actionHandlerArgs={actionHandlerArgs}
          itemIds={itemIds}
          onSelect={onSelectAction}
        >
          {targetStorableColumnWithIndex.column.name}
        </ArchiveOption>
      );
    } else {
      return (
        <Option<InventoryActionHandlerArgs<TTableMetadata, TRowMetadata>>
          key={`${actionKey}-${targetStorableColumnWithIndex.column.name}-${targetStorableColumnWithIndex.column.provenanceUuid}`}
          value={actionHandlerArgs}
          onSelect={onSelectAction}
          isDisabled={itemIds.length === 0}
          tooltipTitle={itemIds.length === 0 ? 'No inventory items in this column' : undefined}
        >
          {targetStorableColumnWithIndex.column.name}
        </Option>
      );
    }
  }

  function columnIsAnyInventoryOrContainerField(column: ColumnWithPositionalIndex): boolean {
    if (isContainersStructuredTableColumn(column.column)) {
      return true;
    }
    const matchingSchemaFields = props.structuredTable.schemas
      .flatMap((schema) => schema.fields)
      .filter(({apiId}) => apiId === column.column.schemaFieldApiId);
    if (matchingSchemaFields.length === 0) {
      // Schema field may have been deleted
      return false;
    }
    const columnSchemaField = matchingSchemaFields[0];
    if (!isRequiredLinkStorableSchemaOrAnyInventory(columnSchemaField.requiredLink)) {
      return false;
    }
    const storableSchema = columnSchemaField.requiredLink.storableSchema;
    return storableSchema == null || APIUtil.isContainerSchema(storableSchema.id);
  }

  const allAvailableInventoryActions = [
    InventoryAction.UPDATE_RESTRICTED_SAMPLE_USERS,
    InventoryAction.RESERVE,
    InventoryAction.CHECK_OUT,
    InventoryAction.CHECK_IN,
    InventoryAction.MOVE_STORABLES,
    InventoryAction.PRINT_LABELS,
    InventoryAction.ARCHIVE,
  ];
  const nestedInventoryActions = [InventoryAction.UPDATE_RESTRICTED_SAMPLE_USERS];
  const containerOnlyActions = [
    InventoryAction.CHECK_OUT,
    InventoryAction.CHECK_IN,
    InventoryAction.RESERVE,
    InventoryAction.UPDATE_RESTRICTED_SAMPLE_USERS,
  ];

  function renderTargetStorableColumnsSubmenu(actionKey: InventoryAction): JSX.Element {
    const filteredTargetableStorableColumns = containerOnlyActions.includes(actionKey)
      ? targetableStorableColumns.filter((column) => columnIsAnyInventoryOrContainerField(column))
      : targetableStorableColumns;
    if (filteredTargetableStorableColumns.length === 0) {
      return renderNoTargetStorableColumnsFoundSubmenu();
    }
    return (
      <Menu dropLeft={true}>
        <Section>
          <SectionHeader>Column</SectionHeader>
          {filteredTargetableStorableColumns.map((column) =>
            renderTargetStorableColumnOption(column, actionKey)
          )}
        </Section>
      </Menu>
    );
  }

  function renderOption(
    optionKey: InventoryAction,
    icon: BenchlingIconName | undefined,
    optionText: string
  ): JSX.Element {
    return (
      <Option key={optionKey} icon={icon}>
        {optionText}
        {renderTargetStorableColumnsSubmenu(optionKey)}
      </Option>
    );
  }

  const availableNonNestedOptionsMap: {
    [k in InventoryAction]?: {icon: BenchlingIconName; optionText: string};
  } = {
    [InventoryAction.RESERVE]: {icon: 'check-in-check-out', optionText: 'Reserve'},
    [InventoryAction.CHECK_OUT]: {icon: 'check-in-check-out', optionText: 'Check out'},
    [InventoryAction.CHECK_IN]: {icon: 'check-in-check-out', optionText: 'Check in'},
    [InventoryAction.ARCHIVE]: {icon: 'archive', optionText: 'Archive'},
    [InventoryAction.MOVE_STORABLES]: {icon: 'move-box', optionText: 'Move'},
    [InventoryAction.PRINT_LABELS]: {icon: 'format-print', optionText: 'Print labels'},
  };
  const availableNestedOptionsMap: {
    [k: string]: {
      icon: BenchlingIconName;
      options: {[j in InventoryAction]?: {icon?: BenchlingIconName; optionText: string}};
    };
  } = {
    'Edit field': {
      icon: 'edit',
      options: {
        [InventoryAction.UPDATE_RESTRICTED_SAMPLE_USERS]: {
          icon: undefined,
          optionText: getRestrictedSampleUsersHeaderText(),
        },
      },
    },
  };
  const allSupportedOptions = props.hiddenOptions
    ? allAvailableInventoryActions.filter((action) => !props.hiddenOptions!.includes(action))
    : allAvailableInventoryActions;
  const [supportedNestedOptions, supportedNonNestedOptions] = partition(
    allSupportedOptions,
    (option: InventoryAction) => nestedInventoryActions.includes(option)
  );

  const menu = (
    <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM} dropLeft={true}>
      <Section>
        <SectionHeader>Inventory actions</SectionHeader>
        {Object.entries(availableNestedOptionsMap).map(([parentOptionText, optionConfig]) => {
          const childOptions = Object.keys(optionConfig.options).filter((option: InventoryAction) =>
            supportedNestedOptions.includes(option)
          );
          return childOptions.length ? (
            <Option key={parentOptionText} icon={optionConfig.icon}>
              {parentOptionText}
              <Menu dropLeft={true}>
                {childOptions.map((optionKey: InventoryAction) =>
                  renderOption(
                    optionKey,
                    optionConfig.options[optionKey]!.icon,
                    optionConfig.options[optionKey]!.optionText
                  )
                )}
              </Menu>
            </Option>
          ) : null;
        })}
        {supportedNonNestedOptions.map((optionKey: InventoryAction) =>
          renderOption(
            optionKey,
            availableNonNestedOptionsMap[optionKey]!.icon,
            availableNonNestedOptionsMap[optionKey]!.optionText
          )
        )}
      </Section>
    </Menu>
  );

  const buttonDisabledTooltip: string | null = (() => {
    if (!isInExecutableCanvas(props)) {
      return 'Inventory actions not allowed in templates or config documents';
    }
    if (props.forceEnableButton) {
      return null;
    }
    if (props.tableStatus === TableIndicatorStatus.ERROR) {
      return 'Fix errors and submit or cancel any edits to perform inventory actions';
    }
    if (
      props.editStatus === StructuredTableEditStatus.EDITING ||
      props.tableStatus === TableIndicatorStatus.UNSUBMITTED_CHANGES
    ) {
      return 'Submit or cancel edits to perform inventory actions';
    }
    if (props.tableStatus === TableIndicatorStatus.OUT_OF_DATE) {
      return 'Refresh table to perform inventory actions';
    }
    if (!props.structuredTable.rows.some((row) => row.cells.some((cell) => !TableUtil.isCellEmpty(cell)))) {
      return 'No inventory items in table';
    }
    return null;
  })();

  return (
    <Tooltip title={buttonDisabledTooltip ?? 'Inventory actions'}>
      <Dropdown menu={menu}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MINI}
          className="u-mini-spacing-left"
          disabled={buttonDisabledTooltip != null}
          icon={ModelTypeConstants.CONTAINER}
          iconAltText={buttonDisabledTooltip ?? 'Perform actions on inventory'}
          isDropdown={true}
          dataTestAttrs={{
            component: 'InventoryActionsButton',
            element: 'button',
          }}
        />
      </Dropdown>
    </Tooltip>
  );
}

export function makeInventoryShortcutsActionFunction<TTableMetadata, TRowMetadata>(
  customInventoryActionHandlers?: {
    [actionKey in InventoryAction]?: (
      handlerArgs: InventoryActionHandlerArgs<TTableMetadata, TRowMetadata>
    ) => void;
  },
  getForceEnableInventoryShortcuts?: (
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    tableStatus?: TableIndicatorStatus
  ) => boolean,
  hiddenOptionsFn?: (
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>
  ) => Array<InventoryAction>,
  hiddenColumnTypes?: Array<TableColumnType>,
  isFeatureEnabled: () => boolean = () => true
): ToolbarButtonActionFunction<TTableMetadata, TRowMetadata> {
  return makeCustomButtonActionFunction({
    getHidden: ({structuredTable}) =>
      !isFeatureEnabled() ||
      getTargetableStorableColumns(structuredTable.columns, hiddenColumnTypes).length === 0,
    renderToolbarButton: ({
      structuredTable,
      computedTable,
      tableStatus,
      entryOrTemplateId,
      liveRowMetadataByRow,
    }) => (
      <BenchlingQuery<CurrentTenantIdAndUserIdQuery>
        query={CURRENT_TENANT_ID_AND_USER_ID_QUERY}
        renderLoading={() => null}
      >
        {(data) => (
          <InventoryActionOnStructuredTableButton
            structuredTable={structuredTable}
            computedTable={computedTable}
            liveRowMetadataByRow={liveRowMetadataByRow}
            customInventoryActionHandlers={customInventoryActionHandlers}
            tableStatus={tableStatus}
            forceEnableButton={
              getForceEnableInventoryShortcuts != null
                ? getForceEnableInventoryShortcuts(structuredTable, tableStatus)
                : false
            }
            analyticsMetadata={{
              actionOrigin: structuredTableActionOrigin(structuredTable),
              actionMethod: ActionMethod.INVENTORY_SHORTCUTS_ACTION_FUNCTION,
              [getEntryOrFileIdKey(entryOrTemplateId, true)]: entryOrTemplateId,
            }}
            currentTenantId={data.currentTenant.id}
            currentUserId={data.currentUser?.id ?? 'MISSING_USER_ID'}
            entryOrTemplateId={entryOrTemplateId}
            hiddenOptions={hiddenOptionsFn?.(structuredTable)}
            hiddenColumnTypes={hiddenColumnTypes}
          />
        )}
      </BenchlingQuery>
    ),
    renderOverflowMenuItem: unsupportedOverflowMenuItem,
  });
}
